import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { InsightChatEvent, InsightChatItem } from '@wundo/shared/insightChat.ts';
import { fetchRunChat } from '@/lib/insights.ts';
import { useInsightChatSocket } from '@/lib/useInsightChatSocket.ts';
import { upsertChatItem } from './upsertChatItem.ts';

export interface RunChat {
  items: InsightChatItem[];
  /** Answers still being written, by message id, in the order they started. */
  streaming: { id: string; text: string }[];
  isPending: boolean;
  isError: boolean;
  /** Show an item straight away — the question just asked — before the socket echoes it. */
  addItem: (item: InsightChatItem) => void;
}

/**
 * A run's conversation, kept live. It is read once and then carried on by the
 * socket: new items are added, an answer grows as it is written, and the run
 * itself is read again whenever it says it changed.
 *
 * When the analyst stops working the whole conversation is read again, so
 * anything said while the socket was briefly down is not lost.
 */
export function useRunChat(
  runId: string,
  conversationId: string | null | undefined,
  working: boolean,
): RunChat {
  const queryClient = useQueryClient();
  const [streaming, setStreaming] = useState<{ id: string; text: string }[]>([]);

  const chat = useQuery({
    queryKey: ['insightRunChat', runId],
    queryFn: () => fetchRunChat(runId),
  });

  const addItem = useCallback(
    (item: InsightChatItem) => {
      queryClient.setQueryData<InsightChatItem[]>(['insightRunChat', runId], (items) =>
        upsertChatItem(items ?? [], item),
      );
      setStreaming((current) => current.filter((reply) => reply.id !== item.id));
    },
    [queryClient, runId],
  );

  const onEvent = useCallback(
    (event: InsightChatEvent) => {
      if (event.type === 'run_updated') {
        void queryClient.invalidateQueries({ queryKey: ['insightRun', runId] });
        return;
      }

      if (event.type === 'item') {
        addItem(event.item);
        return;
      }

      setStreaming((current) =>
        current.some((reply) => reply.id === event.message_id)
          ? current.map((reply) =>
              reply.id === event.message_id
                ? { ...reply, text: reply.text + event.text }
                : reply,
            )
          : [...current, { id: event.message_id, text: event.text }],
      );
    },
    [queryClient, runId, addItem],
  );

  const onReconnect = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['insightRun', runId] });
    void queryClient.invalidateQueries({ queryKey: ['insightRunChat', runId] });
  }, [queryClient, runId]);

  useInsightChatSocket(conversationId, onEvent, onReconnect);

  const wasWorking = useRef(working);
  useEffect(() => {
    if (wasWorking.current && !working) {
      setStreaming([]);
      void queryClient.invalidateQueries({ queryKey: ['insightRunChat', runId] });
    }
    wasWorking.current = working;
  }, [working, queryClient, runId]);

  return {
    items: chat.data ?? [],
    streaming,
    isPending: chat.isPending,
    isError: chat.isError,
    addItem,
  };
}
